import React, { useEffect, useState } from "react";
import axios from "axios";
import { useQuery, useMutation } from "react-query";
import { useLocation } from "react-router";
import {
  Row,
  Container,
  Nav,
  Tab,
  Col,
  Form,
  Button,
  Spinner,
  Modal,
} from "react-bootstrap";
import Select, { createFilter } from "react-select";
import { optimizeSelect } from "../../components/select";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import { t } from "../../localization/localization";

import profilePlaceholder from "../../assets/profile_ph.jpg";
import "./editProfile.css";

import COUNTRIES from "../../data/countries";

export const useQueryParams = () => new URLSearchParams(useLocation().search);

// API
// https://gcris.iyte.edu.tr/gcris-api
// http://localhost:8085
export const get = async (location) =>
  axios
    .get(window._env_.BASE_URL + "/gcris-api" + location)
    .then((response) => response.data)
    .catch((error) => {
      throw error.response ? error.response.data : error;
    });

export const put = async (location, body) =>
  axios
    .put(window._env_.BASE_URL + "/gcris-api" + location, body)
    .then((response) => response.data)
    .catch((error) => {
      throw error.response ? error.response.data : error;
    });

// Profile API
export const getProfile = async (crisId) =>
  get(`/researcher/${crisId}/profile`);

export const updateProfile = async (crisId, data) =>
  put(`/researcher/${crisId}/profile`, data);

export const getOuValues = async () => get(`/org-unit/ouvalues`);

// Hooks
export const useProfile = (crisId) =>
  useQuery(`profile::${crisId}`, () => getProfile(crisId));

export const useUpdateProfile = (crisId) =>
  useMutation((data) => updateProfile(crisId, data), {
    mutationKey: `updateProfile::${crisId}`,
  });

export const useOuValues = () => useQuery(`ouValues`, () => getOuValues());

/**
 *
 * Photo (Image) [???]
 *
 * Full Name (String) ["Full Name"] +
 * Variants (String[]) ["Variants"]+
 * Country (Country) ["Country"]+
 *
 * Main Affiliation (Organization) ["Main Affiliation"] oupointer
 * Email (Email) ["Email"] +
 * Other Emails (Email[]) ["Other emails"] +
 * ORCID (String) ["ORCID"] +
 * Google Scholar Link (String) ["Link to Google Profile"]+
 * Google Scholar ID (String) ["Google Scholar ID"]+
 * Scopus Author ID (String) ["Scopus Author ID"]+
 * Researcher ID (String) ["Researcher ID"] +
 * Personal Site Description (String) ["Personal Site"] +
 * Personal Site Href (String) ["Personal Site"] +
 *
 * Affiliations (Affiliation[]) [???]+
 *  Organization (Organization)
 *  Role (String)
 *  Start Date (Date)
 *  End Date (Date)
 *
 * Educations (Education[]) [???]+
 *  University (University)
 *  Program (String)
 *  Start Date (Date)
 *  End Date (Date)
 *
 * Biography (String) ["Biography"]+
 * Biography Native (String) ["Biography native"]+
 *
 * Institution Registration ID (int) ["Institution Registration ID"] +
 * Working Groups (WorkingGroup[]) ["Working groups"] oupointer
 */

const ListInput = ({ label, type = "text", list, setList }) => {
  const onAdd = () => {
    setList([...list, ""]);
  };

  const onDelete = (index) => {
    let arr = [...list];
    arr.splice(index, 1);
    setList(arr);
  };

  const onChange = (index, text) => {
    let arr = [...list];
    arr[index] = text;
    setList(arr);
  };

  return (
    <Form.Group as={Row} controlId={label}>
      <Form.Label column sm={2}>
        {label}
      </Form.Label>
      <Col sm={10}>
        {list.map((element, index) => (
          <Row key={index} style={{ marginBottom: 8 }}>
            <Col sm={10}>
              <Form.Control
                type={type}
                value={element}
                onChange={(e) => onChange(index, e.target.value)}
              />
            </Col>
            <Col sm={2}>
              <Button style={{ width: "100%" }} onClick={() => onDelete(index)}>
                Del
              </Button>
            </Col>
          </Row>
        ))}
        <Row>
          <Col sm={2}>
            <Button style={{ width: "100%" }} onClick={() => onAdd()}>
              Add
            </Button>
          </Col>
        </Row>
      </Col>
    </Form.Group>
  );
};

const options = {
  sizePerPage: 10,
  hideSizePerPage: true,
  hidePageListOnlyOnePage: true,
};

const sortAndIndexArr = (arr) => {
  const temp = [...arr];
  temp.sort((a, b) => a.startDate?.localeCompare(b.startDate));
  return temp.map((e, i) => ({ ...e, index: i + 1 }));
};

const ResearcherEditProfile = () => {
  const queryParams = useQueryParams();
  const crisId = queryParams.get("crisId");

  const [fullName, setFullName] = useState(""); // working
  const [variants, setVariants] = useState([]); // working
  const [country, setCountry] = useState(null); // working
  const [image, setImage] = useState(null);

  const [mainAffiliation, setMainAffiliation] = useState(null); // working
  const [email, setEmail] = useState(""); // working
  const [otherEmails, setOtherEmails] = useState([]); // working
  const [orcid, setOrcid] = useState(""); // working
  const [googleScholarLink, setGoogleScholarLink] = useState("");
  const [googleScholarId, setGoogleScholarId] = useState("");
  const [scopusAuthorId, setScopusAuthorId] = useState("");
  const [researcherId, setResearcherId] = useState("");
  const [websiteDesc, setWebsiteDesc] = useState("");
  const [websiteHref, setWebsiteHref] = useState("");

  const [affiliations, setAffiliations] = useState([]);
  const [educations, setEducations] = useState([]);
  const [biography, setBiography] = useState("");
  const [biographyNative, setBiographyNative] = useState("");

  const [institutionRegistrationId, setInstitutionRegistrationId] =
    useState("");
  const [workingGroups, setWorkingGroups] = useState(null);

  const {
    data: orgunitValues,
    isLoading: ouValuesLoading,
    error: ouValuesError,
  } = useOuValues();
  const ORGUNIT_VALUES = orgunitValues?.map((ou) => ({
    value: ou.id,
    label: ou.textvalue,
    crisId: ou.crisid,
  }));

  // initialize profile
  const {
    data: profile,
    isLoading: profileLoading,
    error: profileError,
  } = useProfile(crisId);

  useEffect(() => {
    if (profile) {
      // console.log(profile);
      setFullName(profile.fullName ?? "");
      setVariants(profile.variants);
      setCountry(profile.country);
      setImage(profile.imageURL);

      setMainAffiliation({
        value: profile.mainAffiliation?.id,
        label: profile.mainAffiliation?.textvalue,
        crisId: profile.mainAffiliation?.crisid,
      });
      setEmail(profile.email ?? "");
      setOtherEmails(profile.otherEmails);
      setOrcid(profile.orcid ?? "");
      setGoogleScholarLink(profile.googleScholarLink ?? "");
      setGoogleScholarId(profile.googleScholarId ?? "");
      setScopusAuthorId(profile.scopusAuthorId ?? "");
      setResearcherId(profile.researcherId ?? "");
      setWebsiteDesc(profile.websiteDesc ?? "");
      setWebsiteHref(profile.websiteLink ?? "");

      setAffiliations(sortAndIndexArr(profile.affiliations));
      setEducations(sortAndIndexArr(profile.educations));
      setBiography(profile.biography ?? "");
      setBiographyNative(profile.biographyNative ?? "");

      setInstitutionRegistrationId(profile.institutionId ?? "");
      setWorkingGroups(
        profile.workingGroups.map((wg) => ({
          value: wg.id,
          label: wg.textvalue,
          crisId: wg.crisid,
        }))
      );
    }
  }, [profile]);

  // update profile
  const {
    mutate,
    error: updateError,
    isSuccess: updateSuccess,
    isLoading: updateLoading,
  } = useUpdateProfile(crisId);

  useEffect(() => {
    console.log(updateError);
  }, [updateError]);

  useEffect(() => {
    console.log(updateSuccess);
  }, [updateSuccess]);

  const onSubmit = () => {
    const data = {
      fullName,
      variants,
      country,
      // imageURL,
      mainAffiliation: {
        id: mainAffiliation.value,
        textvalue: mainAffiliation.label,
        crisid: mainAffiliation.crisId,
      },
      email,
      otherEmails,
      orcid,
      googleScholarLink,
      googleScholarId,
      scopusAuthorId,
      researcherId,
      websiteDesc,
      websiteLink: websiteHref,
      affiliations,
      educations,
      biography,
      biographyNative,
      institutionId: institutionRegistrationId,
      workingGroups: workingGroups.map((wg) => ({
        id: wg.value,
        textvalue: wg.label,
        crisid: wg.crisId,
      })),
    };
    console.log(data);
    mutate(data);
  };

  // affiliation
  const [showAddAffiliationModal, setShowAddAffiliationModal] = useState(false);
  const [editAffiliationModalObj, setEditAffiliationModalObj] = useState(null);

  const onAddAffiliation = (affiliation) => {
    const temp = [...affiliations, affiliation];
    setAffiliations(sortAndIndexArr(temp));
  };

  const onEditAffiliation = (index, affiliation) => {
    const tempAffiliations = [...affiliations].filter((a) => a.index !== index);
    setAffiliations(sortAndIndexArr([...tempAffiliations, affiliation]));
  };

  const onDeleteAffiliation = (index) => {
    const tempAffiliations = [...affiliations];
    setAffiliations(
      sortAndIndexArr(tempAffiliations.filter((a) => a.index !== index))
    );
  };

  const affiliationColumns = [
    {
      dataField: "index",
      text: "#",
      sort: true,
      headerStyle: { width: "60px" },
    },
    {
      dataField: "role",
      text: "Role",
      sort: true,
    },
    {
      dataField: "organization",
      text: "Organization",
      sort: true,
    },
    {
      dataField: "startDate",
      text: "Start Date",
      sort: true,
      headerStyle: { width: "150px" },
      formatter: (cell, row) => {
        if (cell) return cell.split(" ")[0];
        return "";
      },
    },
    {
      dataField: "endDate",
      text: "End Date",
      sort: true,
      headerStyle: { width: "150px" },
      formatter: (cell, row) => {
        if (cell) return cell.split(" ")[0];
        return "";
      },
    },
    {
      dataField: "id",
      text: "Actions",
      isDummyField: true,
      formatExtraData: affiliations,
      formatter: (cell, row, rowIndex, extraData) => {
        return (
          <Row>
            <Button onClick={() => setEditAffiliationModalObj(row)}>
              Edit
            </Button>
            <Button onClick={() => onDeleteAffiliation(row.index)}>
              Delete
            </Button>
          </Row>
        );
      },
    },
  ];

  // education
  const [showAddEducationModal, setShowAddEducationModal] = useState(false);
  const [editEducationModalObj, setEditEducationModalObj] = useState(null);

  const onAddEducation = (education) => {
    const temp = [...educations, education];
    setEducations(sortAndIndexArr(temp));
  };

  const onEditEducation = (index, education) => {
    const tempEducations = [...educations].filter((a) => a.index !== index);
    setEducations(sortAndIndexArr([...tempEducations, education]));
  };

  const onDeleteEducation = (index) => {
    const tempEducations = [...educations];
    setEducations(
      sortAndIndexArr(tempEducations.filter((a) => a.index !== index))
    );
  };

  const educationColumns = [
    {
      dataField: "index",
      text: "#",
      sort: true,
      headerStyle: { width: "60px" },
    },
    {
      dataField: "program",
      text: "Program",
      sort: true,
    },
    {
      dataField: "university",
      text: "University",
      sort: true,
    },
    {
      dataField: "startDate",
      text: "Start Date",
      sort: true,
      headerStyle: { width: "150px" },
      formatter: (cell, row) => {
        if (cell) return cell.split(" ")[0];
        return "";
      },
    },
    {
      dataField: "endDate",
      text: "End Date",
      sort: true,
      headerStyle: { width: "150px" },
      formatter: (cell, row) => {
        if (cell) return cell.split(" ")[0];
        return "";
      },
    },
    {
      dataField: "id",
      text: "Actions",
      isDummyField: true,
      formatExtraData: educations,
      formatter: (cell, row, rowIndex, extraData) => {
        return (
          <Row>
            <Button onClick={() => setEditEducationModalObj(row)}>Edit</Button>
            <Button onClick={() => onDeleteEducation(row.index)}>Delete</Button>
          </Row>
        );
      },
    },
  ];

  return (
    <div id="edit-profile-container">
      {profileLoading || ouValuesLoading ? (
        <Spinner animation="border" role="status">
          <span className="sr-only">Loading...</span>
        </Spinner>
      ) : profileError ? (
        <div>{profileError.message}</div>
      ) : ouValuesError ? (
        <div>{ouValuesError.message}</div>
      ) : (
        <>
          <Container>
            <Row>
              <Col>
                <Form>
                  <Row className="edit-section">
                    <Col sm={3} className="profile-image-container">
                      <div className="profile-image-wrapper">
                        <img
                          src={image ? image : profilePlaceholder}
                          className="profile-image"
                        />
                      </div>
                      <Form.Group>
                        <Form.File id="photo" label="Profile Photo" />
                      </Form.Group>
                    </Col>
                    <Col sm={9}>
                      <Row className="edit-section">
                        <Col style={{ textAlign: "right" }}>
                          <Button onClick={onSubmit} disabled={updateLoading}>
                            {updateLoading ? "Saving..." : "Save"}
                          </Button>
                          {updateSuccess && <p>Success!</p>}
                          {updateError && <p>{updateError.message}</p>}
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Form.Group as={Row} controlId="fullName">
                            <Form.Label column sm={2}>
                              Full Name
                            </Form.Label>
                            <Col sm={10}>
                              <Form.Control
                                type="text"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                              />
                            </Col>
                          </Form.Group>
                          <ListInput
                            label="Variants"
                            list={variants}
                            setList={setVariants}
                          />
                          <Form.Group as={Row} controlId="country">
                            <Form.Label column sm={2}>
                              Country
                            </Form.Label>
                            <Col sm={10}>
                              <Select
                                value={COUNTRIES.find(
                                  (c) => c.value === country
                                )}
                                onChange={(e) => setCountry(e.value)}
                                options={COUNTRIES}
                                isSearchable={true}
                                components={optimizeSelect.components}
                                filterOption={createFilter({
                                  ignoreAccents: false,
                                })}
                              />
                            </Col>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Tab.Container
                    id="left-tabs-example"
                    defaultActiveKey="personal-information"
                  >
                    <Row className="edit-section">
                      <Col sm={3} md={2} style={{ marginBottom: "24px" }}>
                        <Nav variant="pills" className="flex-column">
                          <Nav.Item>
                            <Nav.Link eventKey="personal-information">
                              Personal Information
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link eventKey="affiliations">
                              Affiliations
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link eventKey="educations">
                              Educations
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link eventKey="biography">Biography</Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link eventKey="others">Others</Nav.Link>
                          </Nav.Item>
                        </Nav>
                      </Col>
                      <Col sm={9} md={10}>
                        <Tab.Content>
                          <Tab.Pane eventKey="personal-information">
                            <Row>
                              <Col>
                                <div className="wall">
                                  <h5>Personal Information</h5>
                                  <Form.Group
                                    as={Row}
                                    controlId="mainAffiliation"
                                  >
                                    <Form.Label column sm={2}>
                                      Main Affiliation
                                    </Form.Label>
                                    <Col sm={10}>
                                      <Select
                                        value={mainAffiliation}
                                        onChange={setMainAffiliation}
                                        options={ORGUNIT_VALUES}
                                        isClearable={true}
                                        isSearchable={true}
                                        components={optimizeSelect.components}
                                        filterOption={createFilter({
                                          ignoreAccents: false,
                                        })}
                                      />
                                    </Col>
                                  </Form.Group>
                                  <Form.Group as={Row} controlId="email">
                                    <Form.Label column sm={2}>
                                      Email
                                    </Form.Label>
                                    <Col sm={10}>
                                      <Form.Control
                                        type="text"
                                        value={email}
                                        onChange={(e) =>
                                          setEmail(e.target.value)
                                        }
                                      />
                                    </Col>
                                  </Form.Group>
                                  <ListInput
                                    label="Other Emails"
                                    list={otherEmails}
                                    setList={setOtherEmails}
                                  />
                                  <Form.Group as={Row} controlId="orcid">
                                    <Form.Label column sm={2}>
                                      ORCID
                                    </Form.Label>
                                    <Col sm={10}>
                                      <Form.Control
                                        type="text"
                                        value={orcid}
                                        onChange={(e) =>
                                          setOrcid(e.target.value)
                                        }
                                      />
                                    </Col>
                                  </Form.Group>
                                  <Form.Group
                                    as={Row}
                                    controlId="googleScholarId"
                                  >
                                    <Form.Label column sm={2}>
                                      Google Scholar Link
                                    </Form.Label>
                                    <Col sm={10}>
                                      <Form.Control
                                        type="text"
                                        value={googleScholarLink}
                                        onChange={(e) =>
                                          setGoogleScholarLink(e.target.value)
                                        }
                                      />
                                    </Col>
                                  </Form.Group>
                                  <Form.Group
                                    as={Row}
                                    controlId="googleScholarId"
                                  >
                                    <Form.Label column sm={2}>
                                      Google Scholar ID
                                    </Form.Label>
                                    <Col sm={10}>
                                      <Form.Control
                                        type="text"
                                        value={googleScholarId}
                                        onChange={(e) =>
                                          setGoogleScholarId(e.target.value)
                                        }
                                      />
                                    </Col>
                                  </Form.Group>
                                  <Form.Group
                                    as={Row}
                                    controlId="scopusAuthorId"
                                  >
                                    <Form.Label column sm={2}>
                                      Scopus Author ID
                                    </Form.Label>
                                    <Col sm={10}>
                                      <Form.Control
                                        type="text"
                                        value={scopusAuthorId}
                                        onChange={(e) =>
                                          setScopusAuthorId(e.target.value)
                                        }
                                      />
                                    </Col>
                                  </Form.Group>
                                  <Form.Group as={Row} controlId="researcherId">
                                    <Form.Label column sm={2}>
                                      Publons ID
                                    </Form.Label>
                                    <Col sm={10}>
                                      <Form.Control
                                        type="text"
                                        value={researcherId}
                                        onChange={(e) =>
                                          setResearcherId(e.target.value)
                                        }
                                      />
                                    </Col>
                                  </Form.Group>
                                  <Form.Group as={Row} controlId="personalSite">
                                    <Form.Label column sm={2}>
                                      Personal Site Name
                                    </Form.Label>
                                    <Col sm={4}>
                                      <Form.Control
                                        type="text"
                                        value={websiteDesc}
                                        onChange={(e) =>
                                          setWebsiteDesc(e.target.value)
                                        }
                                      />
                                    </Col>
                                    <Form.Label column sm={2}>
                                      Personal Site Href
                                    </Form.Label>
                                    <Col sm={4}>
                                      <Form.Control
                                        type="text"
                                        value={websiteHref}
                                        onChange={(e) =>
                                          setWebsiteHref(e.target.value)
                                        }
                                      />
                                    </Col>
                                  </Form.Group>
                                </div>
                              </Col>
                            </Row>
                          </Tab.Pane>
                          <Tab.Pane eventKey="affiliations">
                            <Row>
                              <Col>
                                <div className="wall">
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "row",
                                      justifyContent: "space-between",
                                      marginBottom: 8,
                                    }}
                                  >
                                    <h5>Affiliations</h5>
                                    <Button
                                      onClick={() =>
                                        setShowAddAffiliationModal(true)
                                      }
                                    >
                                      Add
                                    </Button>
                                  </div>
                                  <BootstrapTable
                                    bootstrap4
                                    striped
                                    wrapperClasses="table-responsive"
                                    keyField="index"
                                    defaultSorted={[
                                      {
                                        dataField: "index",
                                        order: "asc",
                                      },
                                    ]}
                                    data={[...affiliations]}
                                    columns={affiliationColumns}
                                    pagination={paginationFactory(options)}
                                  />
                                </div>
                              </Col>
                            </Row>
                            <AddAffiliationModal
                              show={showAddAffiliationModal}
                              setShow={setShowAddAffiliationModal}
                              onAdd={(obj) => onAddAffiliation(obj)}
                              ORGUNIT_VALUES={ORGUNIT_VALUES}
                            />
                            <EditAffiliationModal
                              show={!!editAffiliationModalObj}
                              setShow={setEditAffiliationModalObj}
                              affiliation={editAffiliationModalObj}
                              onSave={(index, obj) =>
                                onEditAffiliation(index, obj)
                              }
                              ORGUNIT_VALUES={ORGUNIT_VALUES}
                            />
                          </Tab.Pane>
                          <Tab.Pane eventKey="educations">
                            <Row>
                              <Col>
                                <div className="wall">
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "row",
                                      justifyContent: "space-between",
                                      marginBottom: 8,
                                    }}
                                  >
                                    <h5>Educations</h5>
                                    <Button
                                      onClick={() =>
                                        setShowAddEducationModal(true)
                                      }
                                    >
                                      Add
                                    </Button>
                                  </div>
                                  <BootstrapTable
                                    bootstrap4
                                    striped
                                    wrapperClasses="table-responsive"
                                    keyField="index"
                                    defaultSorted={[
                                      {
                                        dataField: "index",
                                        order: "asc",
                                      },
                                    ]}
                                    data={educations}
                                    columns={educationColumns}
                                    pagination={paginationFactory(options)}
                                  />
                                </div>
                              </Col>
                            </Row>
                            <AddEducationModal
                              show={showAddEducationModal}
                              setShow={setShowAddEducationModal}
                              onAdd={(obj) => onAddEducation(obj)}
                              ORGUNIT_VALUES={ORGUNIT_VALUES}
                            />
                            <EditEducationModal
                              show={!!editEducationModalObj}
                              setShow={setEditEducationModalObj}
                              education={editEducationModalObj}
                              onSave={(index, obj) =>
                                onEditEducation(index, obj)
                              }
                              ORGUNIT_VALUES={ORGUNIT_VALUES}
                            />
                          </Tab.Pane>
                          <Tab.Pane eventKey="biography">
                            <Row>
                              <Col>
                                <div className="wall">
                                  <h5>Biography</h5>
                                  <Form.Group as={Row} controlId="biography">
                                    <Form.Label column sm={2}>
                                      Biography
                                    </Form.Label>
                                    <Col sm={10}>
                                      <Form.Control
                                        as="textarea"
                                        rows={5}
                                        value={biography}
                                        onChange={(e) =>
                                          setBiography(e.target.value)
                                        }
                                      />
                                    </Col>
                                  </Form.Group>
                                  <Form.Group
                                    as={Row}
                                    controlId="biographyNative"
                                  >
                                    <Form.Label column sm={2}>
                                      Biography Native
                                    </Form.Label>
                                    <Col sm={10}>
                                      <Form.Control
                                        as="textarea"
                                        rows={5}
                                        value={biographyNative}
                                        onChange={(e) =>
                                          setBiographyNative(e.target.value)
                                        }
                                      />
                                    </Col>
                                  </Form.Group>
                                </div>
                              </Col>
                            </Row>
                          </Tab.Pane>
                          <Tab.Pane eventKey="others">
                            <Row>
                              <Col>
                                <div className="wall">
                                  <h5>Others</h5>
                                  <Form.Group
                                    as={Row}
                                    controlId="institutionRegistrationId"
                                  >
                                    <Form.Label column sm={2}>
                                      Institution Registration ID
                                    </Form.Label>
                                    <Col sm={10}>
                                      <Form.Control
                                        type="text"
                                        value={institutionRegistrationId}
                                        onChange={(e) =>
                                          setInstitutionRegistrationId(
                                            e.target.value
                                          )
                                        }
                                      />
                                    </Col>
                                  </Form.Group>
                                  <Form.Group
                                    as={Row}
                                    controlId="workingGroups"
                                  >
                                    <Form.Label column sm={2}>
                                      Working Groups
                                    </Form.Label>
                                    <Col sm={10}>
                                      <Select
                                        value={workingGroups}
                                        onChange={setWorkingGroups}
                                        options={ORGUNIT_VALUES}
                                        isClearable={true}
                                        isSearchable={true}
                                        isMulti={true}
                                        components={optimizeSelect.components}
                                        filterOption={createFilter({
                                          ignoreAccents: false,
                                        })}
                                      />
                                    </Col>
                                  </Form.Group>
                                </div>
                              </Col>
                            </Row>
                          </Tab.Pane>
                        </Tab.Content>
                      </Col>
                    </Row>
                  </Tab.Container>
                  <Row className="edit-section">
                    <Col style={{ textAlign: "right" }}>
                      <Button onClick={onSubmit} disabled={updateLoading}>
                        {updateLoading ? "Saving..." : "Save"}
                      </Button>
                      {updateSuccess && <p>Success!</p>}
                      {updateError && <p>{updateError.message}</p>}
                    </Col>
                  </Row>
                </Form>
              </Col>
            </Row>
          </Container>
        </>
      )}
    </div>
  );
};

const AddAffiliationModal = ({ show, setShow, onAdd, ORGUNIT_VALUES }) => {
  const [role, setRole] = useState("");
  const [organization, setOrganization] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  return (
    <Modal
      show={show}
      onHide={() => setShow(false)}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Add Affiliation</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group as={Row} controlId="role">
          <Form.Label column sm={2}>
            Role
          </Form.Label>
          <Col sm={10}>
            <Form.Control
              type="text"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="organization">
          <Form.Label column sm={2}>
            Organization
          </Form.Label>
          <Col sm={10}>
            <Select
              value={organization}
              onChange={setOrganization}
              options={ORGUNIT_VALUES}
              isClearable={true}
              isSearchable={true}
              components={optimizeSelect.components}
              filterOption={createFilter({
                ignoreAccents: false,
              })}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="startDate">
          <Form.Label column sm={2}>
            Start Date
          </Form.Label>
          <Col sm={10}>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="endDate">
          <Form.Label column sm={2}>
            End Date
          </Form.Label>
          <Col sm={10}>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Col>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShow(false)}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            setShow(false);
            onAdd({
              role,
              organization: organization ? organization.label : null,
              organizationId: organization ? organization.value : null,
              startDate,
              endDate,
            });
            setRole("");
            setOrganization(null);
            setStartDate("");
            setEndDate("");
          }}
        >
          Add
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const EditAffiliationModal = ({
  show,
  setShow,
  affiliation,
  onSave,
  ORGUNIT_VALUES,
}) => {
  const [role, setRole] = useState("");
  const [organization, setOrganization] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    if (affiliation) {
      setRole(affiliation.role);
      setOrganization({
        label: affiliation.organization,
        value: affiliation.organizationId,
      });
      setStartDate(affiliation.startDate ?? "");
      setEndDate(affiliation.endDate ?? "");
    }
  }, [affiliation]);

  return (
    <Modal
      show={show}
      onHide={() => setShow(null)}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Affiliation</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group as={Row} controlId="role">
          <Form.Label column sm={2}>
            Role
          </Form.Label>
          <Col sm={10}>
            <Form.Control
              type="text"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="organization">
          <Form.Label column sm={2}>
            Organization
          </Form.Label>
          <Col sm={10}>
            <Select
              value={organization}
              onChange={setOrganization}
              options={ORGUNIT_VALUES}
              isClearable={true}
              isSearchable={true}
              components={optimizeSelect.components}
              filterOption={createFilter({
                ignoreAccents: false,
              })}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="startDate">
          <Form.Label column sm={2}>
            Start Date
          </Form.Label>
          <Col sm={10}>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="endDate">
          <Form.Label column sm={2}>
            End Date
          </Form.Label>
          <Col sm={10}>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Col>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShow(null)}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            setShow(null);
            onSave(affiliation.index, {
              role,
              organization: organization ? organization.label : null,
              organizationId: organization ? organization.value : null,
              startDate,
              endDate,
            });
          }}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const AddEducationModal = ({ show, setShow, onAdd, ORGUNIT_VALUES }) => {
  const [program, setProgram] = useState("");
  const [university, setUniversity] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  return (
    <Modal
      show={show}
      onHide={() => setShow(false)}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Add Education</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group as={Row} controlId="program">
          <Form.Label column sm={2}>
            Program
          </Form.Label>
          <Col sm={10}>
            <Form.Control
              type="text"
              value={program}
              onChange={(e) => setProgram(e.target.value)}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="university">
          <Form.Label column sm={2}>
            University
          </Form.Label>
          <Col sm={10}>
            <Select
              value={university}
              onChange={setUniversity}
              options={ORGUNIT_VALUES}
              isClearable={true}
              isSearchable={true}
              components={optimizeSelect.components}
              filterOption={createFilter({
                ignoreAccents: false,
              })}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="startDate">
          <Form.Label column sm={2}>
            Start Date
          </Form.Label>
          <Col sm={10}>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="endDate">
          <Form.Label column sm={2}>
            End Date
          </Form.Label>
          <Col sm={10}>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Col>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShow(false)}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            setShow(false);
            onAdd({
              program,
              university: university ? university.label : null,
              universityId: university ? university.value : null,
              startDate,
              endDate,
            });
            setProgram("");
            setUniversity(null);
            setStartDate("");
            setEndDate("");
          }}
        >
          Add
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const EditEducationModal = ({
  show,
  setShow,
  education,
  onSave,
  ORGUNIT_VALUES,
}) => {
  const [program, setProgram] = useState("");
  const [university, setUniversity] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    if (education) {
      setProgram(education.program);
      setUniversity({
        label: education.university,
        value: education.universityId,
      });
      setStartDate(education.startDate ?? "");
      setEndDate(education.endDate ?? "");
    }
  }, [education]);

  return (
    <Modal
      show={show}
      onHide={() => setShow(null)}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Education</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group as={Row} controlId="program">
          <Form.Label column sm={2}>
            Program
          </Form.Label>
          <Col sm={10}>
            <Form.Control
              type="text"
              value={program}
              onChange={(e) => setProgram(e.target.value)}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="university">
          <Form.Label column sm={2}>
            University
          </Form.Label>
          <Col sm={10}>
            <Select
              value={university}
              onChange={setUniversity}
              options={ORGUNIT_VALUES}
              isClearable={true}
              isSearchable={true}
              components={optimizeSelect.components}
              filterOption={createFilter({
                ignoreAccents: false,
              })}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="startDate">
          <Form.Label column sm={2}>
            Start Date
          </Form.Label>
          <Col sm={10}>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="endDate">
          <Form.Label column sm={2}>
            End Date
          </Form.Label>
          <Col sm={10}>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Col>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShow(null)}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            setShow(null);
            onSave(education.index, {
              program,
              university: university ? university.label : null,
              universityId: university ? university.value : null,
              startDate,
              endDate,
            });
          }}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ResearcherEditProfile;
